import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getFirebaseClient, useAuth, useCrypto, InviteLinkBox } from "@workspace/admin-ui";
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc } from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi, type MembershipRow } from "@/lib/callables";
import { useToast } from "@/lib/toast";

interface PharmacyOption {
  id: string;
  displayName: string;
}

/**
 * Chain-wide pharmacist roster: add, edit, suspend/reactivate, revoke, and
 * transfer any pharmacist across every pharmacy in the chain from a single
 * screen. Per-pharmacy management still lives at /pharmacies/:id for admins
 * who prefer that scope; this page aggregates across all of them.
 */
export default function PharmacistsPage() {
  const { membership, user } = useAuth();
  const chainId = membership?.chainId ?? "";
  const { db } = getFirebaseClient();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [pharmacyFilter, setPharmacyFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "suspended">("active");

  const pharmaciesQ = useQuery<PharmacyOption[]>({
    queryKey: ["chain", chainId, "pharmacies", "roster"],
    queryFn: async () => {
      const snap = await getDocs(
        query(collection(db, "chains", chainId, "pharmacies"), orderBy("displayName")),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        displayName: String((d.data() as Record<string, unknown>).displayName ?? d.id),
      }));
    },
    enabled: !!chainId,
  });
  const pharmacies = pharmaciesQ.data ?? [];
  const pharmacyName = (id: string) =>
    pharmacies.find((p) => p.id === id)?.displayName ?? id;

  const membersQ = useQuery({
    queryKey: ["chain", chainId, "pharmacists", "all", statusFilter],
    queryFn: () => chainAdminApi.listMemberships({ chainId, status: statusFilter }),
    enabled: !!chainId,
  });
  const allPharmacists = (membersQ.data?.items ?? []).filter(
    (m) => m.role === "pharmacist",
  );
  const visiblePharmacists =
    pharmacyFilter === "all"
      ? allPharmacists
      : allPharmacists.filter((m) => m.pharmacyId === pharmacyFilter);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacists", "all"] });
    // Keep any open Pharmacy Detail page's membership list in sync too —
    // same adminListMemberships data, separate cache key (see membership
    // cache reconciliation memory note).
    qc.invalidateQueries({ queryKey: ["memberships", chainId] });
  };

  if (!chainId) return null;

  return (
    <>
      <PageHeader
        title="Pharmacists"
        description="Add, edit, suspend, revoke, or transfer pharmacists across every pharmacy in your chain."
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 3, alignItems: { md: "center" }, justifyContent: "space-between" }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Pharmacy</InputLabel>
              <Select
                value={pharmacyFilter}
                label="Pharmacy"
                onChange={(e) => setPharmacyFilter(e.target.value)}
              >
                <MenuItem value="all">All pharmacies</MenuItem>
                {pharmacies.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              size="small"
              value={statusFilter}
              exclusive
              onChange={(_, v) => v && setStatusFilter(v)}
            >
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="suspended">Suspended</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Button variant="contained" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Cancel" : "Add pharmacist"}
          </Button>
        </Stack>

        {showAdd ? (
          <Box sx={{ mb: 3 }}>
            <AddPharmacistForm
              chainId={chainId}
              pharmacies={pharmacies}
              onSuccess={() => {
                setShowAdd(false);
                invalidate();
              }}
            />
          </Box>
        ) : null}

        <Paper>
          {membersQ.isLoading || pharmaciesQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading pharmacists…</Typography>
            </Box>
          ) : visiblePharmacists.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No {statusFilter} pharmacists{pharmacyFilter !== "all" ? " at this pharmacy" : ""}.
              </Typography>
            </Box>
          ) : (
            <>
              {visiblePharmacists.map((m, i, arr) => (
                <Box key={m.membershipId}>
                  <PharmacistRow
                    member={m}
                    chainId={chainId}
                    pharmacyLabel={pharmacyName(m.pharmacyId)}
                    transferTargets={pharmacies.filter((p) => p.id !== m.pharmacyId)}
                    onChange={invalidate}
                    isSelf={!!user && m.uid === user.uid}
                  />
                  {i < arr.length - 1 && <Divider />}
                </Box>
              ))}
            </>
          )}
        </Paper>
      </Box>
    </>
  );
}

function AddPharmacistForm({
  chainId,
  pharmacies,
  onSuccess,
}: {
  chainId: string;
  pharmacies: PharmacyOption[];
  onSuccess: () => void;
}) {
  const { showError } = useToast();
  const { status: cryptoStatus, cryptoRecoveryReason, isReady, reinit, requestRecovery, wrapChainKeyForInvite } = useCrypto();
  const { db } = getFirebaseClient();
  const [pharmacyId, setPharmacyId] = useState(pharmacies[0]?.id ?? "");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [psiNumber, setPsiNumber] = useState("");
  const psiValid = /^[0-9]{5,6}$/.test(psiNumber.trim());
  const [linkResult, setLinkResult] = useState<
    { email: string; link: string; emailSent: boolean } | null
  >(null);

  const m = useMutation({
    mutationFn: async () => {
      const res = await chainAdminApi.issuePharmacistInvite({
        chainId,
        pharmacyId,
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        psiNumber: psiNumber.trim(),
      });
      const encryptedChainKey = await wrapChainKeyForInvite(res.token);
      await setDoc(
        doc(db, "invites", res.inviteId),
        { encryptedChainKey },
        { merge: true },
      );
      return res;
    },
    onSuccess: (res) => {
      // No link is returned when the invitee already has a usable password —
      // the server emails them an access-granted notification instead, and
      // InviteLinkBox renders that case from the empty link. Only treat an
      // empty link as a failure when that email did not go out either.
      if (!res.inviteLink && !res.emailSent) {
        showError(
          "Invite could not be completed — the pharmacist could not be notified. Please try again or contact support.",
        );
        return;
      }
      if (res.inviteLink && !res.emailSent) {
        showError(
          `Account created for ${res.email}, but the credentials email failed to send. Share the link manually.`,
        );
      }
      setLinkResult({ email: res.email, link: res.inviteLink, emailSent: res.emailSent });
      setEmail("");
      setDisplayName("");
      setPsiNumber("");
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const canSubmit =
    !!pharmacyId &&
    email.trim() &&
    displayName.trim() &&
    psiValid &&
    !m.isPending &&
    isReady;

  const cryptoAlert =
    cryptoStatus === "loading" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Loading encryption key…
      </Alert>
    ) : cryptoStatus === "awaiting-delivery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Setting up encryption key — this can take up to 60 s…
      </Alert>
    ) : cryptoStatus === "escrow-recovery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Requesting automatic key recovery…
      </Alert>
    ) : cryptoStatus === "new-device" ? (
      cryptoRecoveryReason === "escrow-missing" ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Recovery unavailable — ask a chain admin who is currently signed in
          to open this pharmacy's detail page and click "Re-deliver key", then
          refresh this page.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void requestRecovery()}>
              Retry Recovery
            </Button>
          }
        >
          Encryption key could not be recovered automatically. Click Retry
          Recovery to try again, or ask a Katana admin to re-deliver the key.
        </Alert>
      )
    ) : cryptoStatus === "error" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        Encryption setup failed. Click Retry to try again.
      </Alert>
    ) : cryptoStatus === "no-chain-key" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        No chain key found. Click Retry, or contact a Katana admin.
      </Alert>
    ) : null;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Add pharmacist
      </Typography>
      {linkResult ? (
        <>
          <InviteLinkBox
            link={linkResult.link}
            email={linkResult.email}
            emailSent={linkResult.emailSent}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={() => { setLinkResult(null); onSuccess(); }}>
              Done
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel>Pharmacy</InputLabel>
              <Select
                value={pharmacyId}
                label="Pharmacy"
                onChange={(e) => setPharmacyId(e.target.value)}
              >
                {pharmacies.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="PSI number"
              value={psiNumber}
              onChange={(e) => setPsiNumber(e.target.value)}
              size="small"
              fullWidth
              error={psiNumber.trim().length > 0 && !psiValid}
              helperText={
                psiNumber.trim().length > 0 && !psiValid
                  ? "Enter a 5 or 6 digit PSI registration number."
                  : "5 or 6 digit PSI registration number"
              }
            />
          </Box>
          {cryptoAlert}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={() => m.mutate()} disabled={!canSubmit}>
              {m.isPending ? "Creating invite…" : "Create invite"}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}

function PharmacistRow({
  member,
  chainId,
  pharmacyLabel,
  transferTargets,
  onChange,
  isSelf,
}: {
  member: MembershipRow;
  chainId: string;
  pharmacyLabel: string;
  transferTargets: PharmacyOption[];
  onChange: () => void;
  isSelf: boolean;
}) {
  const { showSuccess, showError, showWarning } = useToast();
  const { deliverChainKeyToMember, isReady: cryptoReady } = useCrypto();
  const { db } = getFirebaseClient();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<
    "revoke" | "suspend" | "reactivate" | "transfer" | "edit" | null
  >(null);
  const [reason, setReason] = useState("");
  const [toPharmacyId, setToPharmacyId] = useState("");
  const [editName, setEditName] = useState(member.displayName ?? "");
  const [editPsi, setEditPsi] = useState("");

  const memberKeyQ = useQuery({
    queryKey: ["memberKey", chainId, member.uid],
    queryFn: async () => {
      const snap = await getDoc(doc(db, "chains", chainId, "memberKeys", member.uid));
      if (!snap.exists()) return { exists: false, autoDelivered: false };
      // A slot with an empty pharmacyId is one the pharmacist's own read rule
      // denies, so it is not a delivered key however much it looks like one.
      // Reporting it as absent keeps the badge honest and keeps the "Deliver
      // E2EE key" button available to fix it.
      const pharmacyId = snap.data()?.pharmacyId;
      if (typeof pharmacyId !== "string" || pharmacyId === "") {
        return { exists: false, autoDelivered: false };
      }
      const autoDelivered = snap.data()?.autoDelivered === true;
      return { exists: true, autoDelivered };
    },
    enabled: !!chainId && !!member.uid,
    staleTime: 30_000,
  });

  const deliverKeyM = useMutation({
    mutationFn: () => deliverChainKeyToMember(member.uid, chainId, member.pharmacyId),
    onSuccess: (delivered) => {
      if (delivered) {
        showSuccess(`E2EE chain key delivered to ${member.email}`);
        qc.invalidateQueries({ queryKey: ["memberKey", chainId, member.uid] });
      } else {
        showWarning(
          `${member.email}'s device key hasn't synced to the server yet. This usually means they haven't opened the app, or their last app open hasn't finished syncing — ask them to open the app again (with a connection) and retry in a few minutes.`,
        );
      }
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Key delivery failed"),
  });
  const isSuspended = member.status === "suspended";

  const revokeM = useMutation({
    mutationFn: () =>
      chainAdminApi.revokeMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
        disableAuth: true,
      }),
    onSuccess: (res) => {
      showSuccess(`Revoked ${member.email}`);
      if (res.authError) showWarning(`Auth disable: ${res.authError}`);
      setConfirming(null);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const suspendM = useMutation({
    mutationFn: () =>
      chainAdminApi.suspendMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Suspended ${member.email}`);
      setConfirming(null);
      setReason("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const reactivateM = useMutation({
    mutationFn: () => chainAdminApi.reactivateMembership({ membershipId: member.membershipId }),
    onSuccess: () => {
      showSuccess(`Reactivated ${member.email}`);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const transferM = useMutation({
    mutationFn: () =>
      chainAdminApi.transferMembership({
        membershipId: member.membershipId,
        toChainId: chainId,
        toPharmacyId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Transferred ${member.email}`);
      setConfirming(null);
      setReason("");
      setToPharmacyId("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const updateM = useMutation({
    mutationFn: () =>
      chainAdminApi.updatePharmacist({
        membershipId: member.membershipId,
        displayName: editName.trim() || undefined,
        psiNumber: editPsi.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Updated ${member.email}`);
      setConfirming(null);
      setEditPsi("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {member.displayName || member.email || "(no name)"}
            </Typography>
            <Chip label={pharmacyLabel} size="small" variant="outlined" color="primary" />
            {isSuspended && <Chip label="suspended" size="small" color="warning" />}
            {memberKeyQ.data?.exists === true ? (
              memberKeyQ.data.autoDelivered ? (
                <Chip label="🔑 auto-delivered" size="small" color="success" variant="outlined" />
              ) : (
                <Chip label="🔑 E2EE" size="small" color="success" variant="outlined" />
              )
            ) : memberKeyQ.data?.exists === false ? (
              <Chip label="no key" size="small" color="default" variant="outlined" />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {member.email || "(no email)"}
          </Typography>
          {isSuspended && member.suspendedReason ? (
            <Typography variant="caption" color="text.secondary" component="p">
              Reason: {member.suspendedReason}
            </Typography>
          ) : null}
        </Box>
        {!confirming ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditName(member.displayName ?? "");
                setEditPsi("");
                setConfirming("edit");
              }}
            >
              Edit
            </Button>
            {!isSuspended && cryptoReady && memberKeyQ.data?.exists === false ? (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => deliverKeyM.mutate()}
                disabled={deliverKeyM.isPending}
              >
                {deliverKeyM.isPending ? "Delivering…" : "Deliver E2EE key"}
              </Button>
            ) : null}
            {!isSelf ? (
              isSuspended ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setConfirming("reactivate")}
                  disabled={reactivateM.isPending}
                >
                  Reactivate
                </Button>
              ) : (
                <Button size="small" variant="outlined" onClick={() => setConfirming("suspend")}>
                  Suspend
                </Button>
              )
            ) : null}
            {!isSuspended && transferTargets.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setToPharmacyId(transferTargets[0]!.id);
                  setConfirming("transfer");
                }}
              >
                Transfer
              </Button>
            ) : null}
            <Button size="small" variant="outlined" color="error" onClick={() => setConfirming("revoke")}>
              Revoke
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {confirming === "edit" ? (
        <Box
          sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "primary.light", bgcolor: "primary.50" }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
              <TextField
                label="Full name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="PSI number"
                helperText="Leave blank to keep unchanged · 5–6 digits"
                value={editPsi}
                onChange={(e) => setEditPsi(e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" size="small" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => updateM.mutate()}
                disabled={!editName.trim() || updateM.isPending}
              >
                {updateM.isPending ? "Saving…" : "Save changes"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : confirming === "transfer" ? (
        <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "primary.light", bgcolor: "primary.50" }}>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Destination pharmacy</InputLabel>
              <Select
                value={toPharmacyId}
                label="Destination pharmacy"
                onChange={(e) => setToPharmacyId(e.target.value)}
              >
                {transferTargets.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.displayName} (/{pt.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Reason (recorded on history record)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder="e.g. Relocated to the city-centre branch"
              size="small"
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Ends this membership and creates a new one at the destination,
              linked back to it. Prior consultations stay rooted at this
              pharmacy.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                  setToPharmacyId("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => transferM.mutate()}
                disabled={!toPharmacyId || transferM.isPending}
              >
                {transferM.isPending ? "Transferring…" : "Confirm transfer"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : confirming === "reactivate" ? (
        <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "primary.light", bgcolor: "primary.50" }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Reactivate <strong>{member.email}</strong>? They will regain access to this pharmacy.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" size="small" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                reactivateM.mutate();
                setConfirming(null);
              }}
              disabled={reactivateM.isPending}
            >
              {reactivateM.isPending ? "Reactivating…" : "Confirm reactivate"}
            </Button>
          </Stack>
        </Box>
      ) : confirming ? (
        <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "error.light", bgcolor: "error.50" }}>
          <Stack spacing={2}>
            <TextField
              label={
                confirming === "revoke"
                  ? "Reason (recorded permanently on history record)"
                  : "Reason (recorded on membership; user keeps their account)"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder={confirming === "revoke" ? "e.g. Left the pharmacy" : "e.g. On extended leave"}
              size="small"
              fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => (confirming === "revoke" ? revokeM.mutate() : suspendM.mutate())}
                disabled={revokeM.isPending || suspendM.isPending}
              >
                {confirming === "revoke"
                  ? revokeM.isPending ? "Revoking…" : "Confirm revoke"
                  : suspendM.isPending ? "Suspending…" : "Confirm suspend"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
